// types.ts — geteilte Typen + Helfer des Kalender-Neubaus (`/termine`).
// Definiert in SP1 als verbindliche, stabile Contracts für SP2–SP7.

import type { TerminArt, TerminMetaDto } from "../../services/calendarTerminService";

/**
 * Sidecar-Meta in der View-Schicht. 1:1 die in OutlookTermine bewährte
 * Teilmenge (terminArt/blockTyp/pflichtBlock/abwesenheitTyp/kundeId), hier als
 * wiederverwendbarer Typ für das neue Grid + die Folge-Sub-Pläne.
 * `farbe` (SP6/A10): individuelle Preset-Farbe — schlägt im Grid/Popover die
 * Art-Farbe (Priorität farbe > Art > Quelle, siehe farbeAusMeta).
 */
export type CalendarMeta = Pick<
    TerminMetaDto,
    | "terminArt"
    | "blockTyp"
    | "pflichtBlock"
    | "abwesenheitTyp"
    | "kundeId"
    | "farbe"
> & {
    /**
     * K9 — an welchem Vorgang hängt der Termin? Speist Kennzeichnung, Tooltip
     * und den Filter „Nur Termine mit Vorgang". BEWUSST optional: Legacy-Mounts
     * und Test-Doubles bauen `CalendarMeta` ohne dieses Feld, und ein fehlender
     * Vorgang ist dasselbe wie kein Vorgang.
     */
    vorgangId?: number | null;
    /** Titel des Vorgangs; null ohne Leserecht — dann bleibt nur die Nummer. */
    vorgangTitel?: string | null;
};

/** K9 — hängt dieser Termin an einem Vorgang? */
export function haengtAnVorgang(meta?: CalendarMeta | null): boolean {
    return meta?.vorgangId != null;
}

/**
 * Ein View-Event für das Grid. Form = 1:1 die bewährte ViewEvent-Struktur aus
 * OutlookTermine.tsx, hier als wiederverwendbarer, exportierter Typ. SP5 füllt
 * die Liste über `listEventsForMailboxes` + `metaBatch`; SP1 reicht `[]` durch.
 */
export interface CalendarGridEvent {
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    location: string;
    /** = source.address — Basis für das WRITE-Gating (SP3/SP5). */
    mailbox: string;
    source: { address: string; displayName: string; color: string };
    /** terminArt etc. (für SP5-Filter/Farbe). undefined = unklassifiziert. */
    meta?: CalendarMeta;
    /**
     * Graph-Instanztyp (SP4, READ). Quelle für einen Serien-Indikator im Grid
     * (Konsistenz C4): der Indikator wird AUS `type`/`seriesMasterId` abgeleitet,
     * NICHT aus `recurrence` — letzteres liefert der calendarView-Instanz-Load
     * nicht zuverlässig, nur `getEvent` beim Öffnen. SP5 befüllt das beim Mapping.
     */
    type?: "singleInstance" | "occurrence" | "exception" | "seriesMaster";
    /** Bei occurrence/exception: Id des Serien-Masters (SP4, für Serien-Icon). */
    seriesMasterId?: string | null;
    /**
     * Graph showAs (free|tentative|busy|oof|workingElsewhere|unknown) —
     * Basis für den Tentative-Strichel-Stil im Grid (SP2 Grid-Lesbarkeit).
     */
    showAs?: string | null;
    /**
     * Graph sensitivity (normal|personal|private|confidential) — PO 27.07.2026.
     *
     * Am EIGENEN Termin die Bestätigung, dass die Privat-Markierung sitzt; an
     * einem FREMDEN steht sie an einem bereits maskierten Termin („Privat", ohne
     * Ort/Teilnehmer). Maskiert wird ausschließlich im Backend.
     */
    sensitivity?: string | null;
    /**
     * Teams-Join-Link (falls Online-Meeting) — Basis für das Kamera-Icon am
     * Terminblock (SP2); „Jetzt teilnehmen" nutzt ihn im Peek-Popover (SP4).
     */
    onlineMeetingUrl?: string | null;
    /**
     * Graph iCalUId — postfachübergreifend stabile Termin-Identität (SP3).
     * Dedupe-Schlüssel für Overlay-Dubletten = iCalUId + Start; Serieninstanzen
     * teilen die UID des Serien-Masters (deshalb NIE nur über die UID dedupen).
     */
    iCalUId?: string | null;
    /**
     * Overlay-Dubletten (SP3, Entscheidung 62): die Quellen (Kalender), in denen
     * derselbe Termin ZUSÄTZLICH liegt — die Dedupe-Verlierer. Der Tooltip zeigt
     * sie als „auch in: …" (OutlookKalender.tooltipAccessor).
     */
    ghostSources?: { address: string; displayName: string; color: string }[];
    /**
     * Organisator des Termins (SP4, Peek-Popover A16 — Screenshot-Gap: die
     * Outlook-Referenz zeigt ihn, KMVS bisher nirgends). Alles im List-DTO
     * vorhanden — reines FE-Mapping, keine BE-Änderung.
     */
    organizer?: { name: string; address: string } | null;
    /** Teilnehmer (SP4 zeigt die Anzahl; SP5 baut die kollabierte Chip-Liste). */
    attendees?: { name: string; address: string; type: string; response: string }[];
    /** Graph bodyPreview (Plain-Text) — Kurzvorschau im Peek-Popover (SP4). */
    bodyPreview?: string;
    /**
     * Nur an Terminen der Ebene „Termine meiner Kontakte" (PO 28.09.2026): der
     * Kontakt, über den die Nutzerin den Termin sieht. Gesetzt ⇒ der Termin
     * liegt in einem Postfach ohne Leserecht — nur ansehen und Notizen
     * schreiben, nicht verschieben, nicht verknüpfen.
     */
    ueberKontakt?: { contactId: number; name: string; weg: "zuweisung" | "teilnehmer" } | null;
}

/** Serien-Indikator AUS type/seriesMasterId (C4), nie aus recurrence. */
export function isSeriesEvent(ev: Pick<CalendarGridEvent, "type" | "seriesMasterId">): boolean {
    return (
        ev.type === "occurrence" ||
        ev.type === "exception" ||
        ev.type === "seriesMaster" ||
        !!ev.seriesMasterId
    );
}

/**
 * D2 (Konsistenz-Review) — EINE gemeinsame Quelle für die `terminArt`-Klassen,
 * damit SP2/SP3/SP5 die Klassifikation nicht jeweils eigen interpretieren.
 *
 * - `block` / `abwesenheit` → immer sichtbar, filterunabhängig (Pflicht-Blöcke,
 *   Abwesenheiten dürfen nie aus den Filtern fallen).
 * - `kundentermin` / `austausch` / `sonstige` → den drei Filtern zugeordnet.
 * - unklassifiziert (kein Sidecar) → SP5 entscheidet (Auto-Klassifikation);
 *   hier als `austausch` behandelt (Default lt. Vorplan), bis SP5 greift.
 *   Bewusst NICHT als `sonstige` (PO 21.09.2026): „Sonstige" ist die bewusste
 *   Aussage eines Menschen, „noch nicht klassifiziert" ist keine — sonst
 *   verschwänden alle frischen Termine, sobald jemand den Sonstige-Filter
 *   abwählt.
 */
export type CalendarTerminClass =
    | "kundentermin"
    | "austausch"
    | "sonstige"
    | "block"
    | "abwesenheit";

/** Leitet die Klasse eines View-Events aus seiner Sidecar-Art ab. */
export function classifyTermin(meta: CalendarMeta | undefined): CalendarTerminClass {
    const art: TerminArt | null | undefined = meta?.terminArt;
    if (art === "kundentermin") return "kundentermin";
    if (art === "sonstige") return "sonstige";
    if (art === "block") return "block";
    if (art === "abwesenheit") return "abwesenheit";
    // 'austausch' explizit + Default für unklassifizierte Events.
    return "austausch";
}

/**
 * Sichtbarkeit unter den Filtern (SP5 nutzt das beim Anwenden auf die Events).
 * `block`/`abwesenheit` sind immer sichtbar; die drei anderen folgen ihrem
 * Filter-Flag.
 */
export function isVisibleUnderFilter(
    cls: CalendarTerminClass,
    filter: {
        kundentermin: boolean;
        austausch: boolean;
        sonstige?: boolean;
        nurMitVorgang?: boolean;
    },
    meta?: CalendarMeta,
): boolean {
    if (cls === "block" || cls === "abwesenheit") return true;
    // K9: „Nur Termine mit Vorgang" schränkt zusätzlich ein. Block und
    // Abwesenheit bleiben wie bisher immer sichtbar (oben schon raus).
    if (filter.nurMitVorgang === true && !haengtAnVorgang(meta)) return false;
    if (cls === "kundentermin") return filter.kundentermin;
    // `sonstige` ist optional: ein Filter-Objekt ohne das Flag (Alt-Zustand aus
    // dem localStorage, ältere Aufrufer) darf nichts ausblenden — nur ein
    // ausdrückliches `false` blendet aus.
    if (cls === "sonstige") return filter.sonstige !== false;
    return filter.austausch;
}
